import { useMediaQuery } from '../hooks/useMediaQuery';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import { PatientStatus, PatientListItem, buildDisplayName } from '../types';
import { getCachedPatients, setCachedPatients } from '../utils/referenceDataCache';
import { getEffectiveHc } from '../utils/patientHc';
import { useThemedStyles } from '../theme/useThemedStyles';
import { formatDate } from '../utils/dateFormat';

const btnStyle: React.CSSProperties = { margin: '0 4px', padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', background: '#1976d2', color: 'white' };
const badgeBase: React.CSSProperties = { display:'inline-block', padding:'2px 8px', borderRadius:12, fontSize:12, fontWeight:600, lineHeight:1, letterSpacing:0.3, minWidth:64, textAlign:'center' };
const statusColors: Record<string, {bg:string;color:string}> = {
  New: { bg:'#1e3a8a', color:'#bfdbfe' }, // Dark blue bg, light blue text
  Active: { bg:'#14532d', color:'#bbf7d0' }, // Dark green bg, light green text
  Inactive: { bg:'#7c2d12', color:'#fed7aa' }, // Dark orange bg, light orange text
  Archived: { bg:'#3f3f46', color:'#e4e4e7' }, // Dark gray bg, light gray text
  Responsible: { bg:'#581c87', color:'#e9d5ff' } // Dark purple bg, light purple text
};
function StatusBadge({ value }: { value: string }) {
  const { t } = useTranslation();

  const sc = statusColors[value] || { bg:'#334155', color:'#f1f5f9' };
  let label = value;
  if (value === 'New') label = t('patientList.pending');
  else if (value === 'Active') label = t('patientList.active');
  else if (value === 'Inactive') label = t('patientList.inactive');
  else if (value === 'Archived') label = t('patientList.archived');
  else if (value === 'Responsible') label = t('patientList.responsible');
  
  return <span style={{...badgeBase, background:sc.bg, color:sc.color}}>{label}</span>;
}
// Age calculation removed (age column no longer displayed)

interface PatientListProps {
  token: string;
  onSelect?: (id: number) => void;
  onCreateNew?: () => void;
  initialViewState: PatientListViewState;
  onViewStateChange: (state: PatientListViewState) => void;
}

export interface PatientListViewState {
  statusFilter: PatientStatus | 'All';
  balanceFilter: 'All' | 'WithBalance' | 'NoBalance';
  searchQuery: string;
  showFilters: boolean;
  sortBy: 'hc' | 'name' | 'status' | 'balance' | '';
  sortDir: 'asc' | 'desc';
  currentPage: number;
}

export const createDefaultPatientListViewState = (): PatientListViewState => ({
  statusFilter: 'Active',
  balanceFilter: 'All',
  searchQuery: '',
  showFilters: false,
  sortBy: '',
  sortDir: 'asc',
  currentPage: 1
});

export default function PatientList({ token, onSelect, onCreateNew, initialViewState, onViewStateChange }: PatientListProps) {
  const { t } = useTranslation();
  const isPhone = useMediaQuery('(max-width: 767px)');
  const theme = useThemedStyles();
  const [patients, setPatients] = useState<PatientListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<PatientStatus | 'All'>(initialViewState.statusFilter);
  const [balanceFilter, setBalanceFilter] = useState<'All' | 'WithBalance' | 'NoBalance'>(initialViewState.balanceFilter);
  const [searchQuery, setSearchQuery] = useState<string>(initialViewState.searchQuery);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState<boolean>(initialViewState.showFilters);
  const [sortBy, setSortBy] = useState<'hc' | 'name' | 'status' | 'balance' | ''>(initialViewState.sortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialViewState.sortDir);
  const [currentPage, setCurrentPage] = useState(initialViewState.currentPage);
  const [itemsPerPage] = useState(50);
  const filterResetReady = useRef(false);
  const currencyFormatter = useMemo(() => new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB', minimumFractionDigits: 2 }), []);

  const processedPatients = useMemo(() => {
    let list = patients;

    // Client-side filtering
    if (statusFilter !== 'All') {
      list = list.filter(p => p.status === statusFilter || p.statusName === statusFilter);
    }

    const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
    if (normalizedSearch) {
      list = list.filter(p => {
        if (String(p.id) === normalizedSearch) return true;
        return [p.hc, p.name, p.firstName, p.secondName, p.firstLastName, p.secondLastName, p.presentedName]
          .some(value => value?.toLocaleLowerCase().includes(normalizedSearch));
      });
    }

    if (balanceFilter === 'WithBalance') {
      list = list.filter(p => (p.balanceDue ?? 0) > 0.0001);
    } else if (balanceFilter === 'NoBalance') {
      list = list.filter(p => (p.balanceDue ?? 0) <= 0.0001);
    }

    if (!sortBy) return list;
    const sorted = [...list];
    sorted.sort((a,b) => {
      if (sortBy === 'hc') return (a.hc||'').localeCompare(b.hc||'', undefined, { sensitivity:'base', numeric: true });
      if (sortBy === 'name') return (a.name||'').localeCompare(b.name||'', undefined, { sensitivity:'base' });
      if (sortBy === 'status') return (a.statusName||a.status).localeCompare(b.statusName||b.status);
      if (sortBy === 'balance') return (a.balanceDue ?? 0) - (b.balanceDue ?? 0);
      return 0;
    });
    if (sortDir === 'desc') sorted.reverse();
    return sorted;
  }, [balanceFilter, patients, searchQuery, sortBy, sortDir, statusFilter]);

  const getPaginatedPatients = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return processedPatients.slice(startIndex, endIndex);
  };

  const totalPages = Math.max(1, Math.ceil(processedPatients.length / itemsPerPage));

  const handleSort = (col: 'hc'|'name'|'status'|'balance') => {
    setSortBy(prev => {
      if (prev === col) { setSortDir(d => d==='asc' ? 'desc' : 'asc'); return prev; }
      setSortDir('asc');
      return col;
    });
  };

  const mapBackendPatient = (p: any): PatientListItem => {
    const nameParts = {
      firstName: p.firstName || '',
      secondName: p.secondName || '',
      firstLastName: p.firstLastName || '',
      secondLastName: p.secondLastName || '',
      presentedName: p.presentedName || ''
    };
    return {
      id: p.id,
      hc: getEffectiveHc(p.hc || p.HC),
      ...nameParts,
      email: p.email,
      dateOfBirth: p.dateOfBirth,
  nextContact: p.nextContact,
      phoneNumber: p.phoneNumber,
      address: p.address,
      status: p.status as PatientStatus,
      statusName: p.statusName || (p.status ? String(p.status) : ''),
      notes: p.notes,
      profilePicturePath: p.profilePicturePath,
      referralSource: p.referralSource,
      responsiblePartyId: p.responsiblePartyId,
      balanceDue: typeof p.balanceDue === 'number' ? p.balanceDue : 0,
      name: buildDisplayName(nameParts)
    };
  };

  const fetchPatients = useCallback((forceRefresh = false) => {
    if (!forceRefresh) {
      const cachedPatients = getCachedPatients<any>();
      if (cachedPatients) {
        setPatients(cachedPatients.map(mapBackendPatient));
        setLoading(false);
        setError('');
        return;
      }
    }

    setLoading(true);
    setError('');
    fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENTS), { headers: getAuthHeaders(token) })
      .then(res => { if (!res.ok) throw new Error('Failed to fetch patients'); return res.json(); })
      .then(data => {
        const mapped = data.map(mapBackendPatient);
        setPatients(mapped);
        setCachedPatients(data);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (!filterResetReady.current) {
      filterResetReady.current = true;
      return;
    }
    setCurrentPage(1);
  }, [statusFilter, balanceFilter, searchQuery, sortBy, sortDir]);

  useEffect(() => {
    if (loading) return;
    setCurrentPage(page => Math.min(Math.max(1, page), totalPages));
  }, [loading, totalPages]);

  useEffect(() => {
    onViewStateChange({
      statusFilter,
      balanceFilter,
      searchQuery,
      showFilters,
      sortBy,
      sortDir,
      currentPage
    });
  }, [balanceFilter, currentPage, onViewStateChange, searchQuery, showFilters, sortBy, sortDir, statusFilter]);

  const themedBtnStyle: React.CSSProperties = { ...btnStyle, ...theme.button, padding: '4px 12px', margin: '0 4px' };
  const themedInputStyle: React.CSSProperties = { ...theme.input, padding: '6px' };
  const headerGridStyle: React.CSSProperties = { borderRight: `1px solid ${theme.border}`, borderBottom: `1px solid ${theme.border}` };
  const bodyGridStyle: React.CSSProperties = { borderRight: `1px solid ${theme.border}`, borderBottom: `1px solid ${theme.border}` };

  if (loading) return <div style={{ color: theme.text }}>{t('patientList.loading')}</div>;
  if (error) return <div style={{color:'#ef4444'}}>{error}</div>;

  return (
    <div style={{ color: theme.text }}>
      <h2>{t('patientList.title')}</h2>
      <div className="responsive-row" style={{display:'flex', gap:12, alignItems:'center', marginBottom:12, flexWrap:'wrap'}}>
        <button type="button" style={themedBtnStyle} onClick={() => setShowFilters(s => !s)}>{showFilters ? t('patientList.hideFilters') : t('patientList.showFilters')}</button>
        <button style={themedBtnStyle} type="button" onClick={() => fetchPatients(true)}>{t('patientList.refresh')}</button>
        {onCreateNew && (
          <button style={{...themedBtnStyle, background:'#2e7d32', borderColor:'#2e7d32'}} type="button" onClick={onCreateNew}>
            {t('patientList.addPatient')}
          </button>
        )}
        {showFilters && (
          <div className="responsive-row" style={{display:'flex', gap:16, flexWrap:'wrap', background:theme.surface, padding:12, border:`1px solid ${theme.border}`, borderRadius:8, boxShadow:theme.shadow, color: theme.text, width: '100%'}}>
            <div style={{display:'flex', flexDirection:'column', gap:4}}>
              <label style={{fontSize:12,fontWeight:600}}>{t('patientList.status')}</label>
              <select 
                value={statusFilter} 
                onChange={e => setStatusFilter(e.target.value as any)}
                style={themedInputStyle}
              >
                <option value="All">{t('patientList.all')}</option>
                <option value="New">{t('patientList.pending')}</option>
                <option value="Active">{t('patientList.active')}</option>
                <option value="Inactive">{t('patientList.inactive')}</option>
                <option value="Archived">{t('patientList.archived')}</option>
                <option value="Responsible">{t('patientList.responsible')}</option>
              </select>
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:4}}>
              <label style={{fontSize:12,fontWeight:600}}>{t('patientList.balance')}</label>
              <select 
                value={balanceFilter} 
                onChange={e => setBalanceFilter(e.target.value as any)}
                style={themedInputStyle}
              >
                <option value="All">{t('patientList.all')}</option>
                <option value="WithBalance">{t('patientList.withBalance')}</option>
                <option value="NoBalance">{t('patientList.noBalance')}</option>
              </select>
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:4, flexGrow: 1, minWidth: '200px'}}>
              <label style={{fontSize:12,fontWeight:600}}>{t('calendar.searchPlaceholder', 'Search')}</label>
              <input 
                value={searchQuery} 
                onChange={e=>setSearchQuery(e.target.value)} 
                placeholder={t('calendar.searchPlaceholder', 'Search...')} 
                style={{ ...themedInputStyle, width: '100%' }} 
              />
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:4, justifyContent:'flex-end'}}>
              <button type="button" style={themedBtnStyle} onClick={()=>{ setSearchQuery(''); setStatusFilter('Active'); setBalanceFilter('All'); }}>{t('patientList.clearFilters')}</button>
            </div>
          </div>
        )}
      </div>
      <div className="responsive-row" style={{marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: theme.text}}>
        <div>{t('patientList.showing', { start: processedPatients.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1, end: Math.min(currentPage * itemsPerPage, processedPatients.length), total: processedPatients.length })}</div>
        <div className="responsive-row" style={{display: 'flex', gap: 8, alignItems: 'center'}}>
          <button 
            type="button" 
            style={{...themedBtnStyle, opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer'}} 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← {t('patientList.previous', 'Previous')}
          </button>
          <span>{t('patientList.page', { current: currentPage, total: totalPages })}</span>
          <button 
            type="button" 
            style={{...themedBtnStyle, opacity: currentPage >= totalPages ? 0.5 : 1, cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'}} 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            {t('patientList.next', 'Next')} →
          </button>
        </div>
      </div>
      <div style={{border:`1px solid ${theme.border}`, borderRadius:8, overflow:'hidden'}}>
        {isPhone && <div className="phone-patient-sort"><label>{t('responsive.sort')}<select aria-label={t('responsive.sort')} value={sortBy} onChange={event => handleSort(event.target.value as 'hc' | 'name' | 'status' | 'balance')}><option value="hc">HC</option><option value="name">{t('patientList.name')}</option><option value="status">{t('patientList.status')}</option><option value="balance">{t('patientList.balance')}</option></select></label><button type="button" onClick={() => handleSort(sortBy || 'name')} aria-label={sortDir === 'asc' ? t('responsive.descending') : t('responsive.ascending')}>{sortDir === 'asc' ? '↑' : '↓'}</button></div>}
        <table className="phone-card-table" style={{borderCollapse:'separate', width:'100%', borderSpacing:0, tableLayout:'fixed'}}>
          <thead style={{position:'sticky', top:0, zIndex:5}}>
            <tr style={{background:theme.surface, color:theme.text}}>
              <th style={{...headerGridStyle, padding:'6px 10px', textAlign:'left', cursor:'pointer', width:'10%'}} onClick={() => handleSort('hc')}>
                HC {sortBy==='hc' ? (sortDir==='asc'?'▲':'▼') : ''}
              </th>
              <th style={{...headerGridStyle, padding:'6px 10px', textAlign:'left', cursor:'pointer', width:'28%'}} onClick={() => handleSort('name')}>
                {t('patientList.name')} {sortBy==='name' ? (sortDir==='asc'?'▲':'▼') : ''}
              </th>
              <th style={{...headerGridStyle, padding:'6px 10px', textAlign:'left', width:'14%'}}>{t('patientList.dob')}</th>
              <th style={{...headerGridStyle, padding:'6px 10px', textAlign:'left', cursor:'pointer', width:'16%'}} onClick={() => handleSort('status')}>
                {t('patientList.status')} {sortBy==='status' ? (sortDir==='asc'?'▲':'▼') : ''}
              </th>
              <th style={{...headerGridStyle, padding:'6px 10px', textAlign:'right', cursor:'pointer', width:'14%'}} onClick={() => handleSort('balance')}>
                {t('patientList.balance')} {sortBy==='balance' ? (sortDir==='asc'?'▲':'▼') : ''}
              </th>
              <th style={{...headerGridStyle, borderRight:'none', padding:'6px 10px', textAlign:'left', width:'28%'}}>{t('patientList.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedPatients().map((p,i) => {
              const rowStyle: React.CSSProperties = { background: i%2===0 ? theme.inputBg : theme.surface, transition:'background .12s', cursor:'pointer', color: theme.text };
              return (
                <tr key={p.id} style={rowStyle} tabIndex={0} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect?.(p.id); } }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = theme.surfaceAlt; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = rowStyle.background as string; }}
                  onClick={() => { if (onSelect) onSelect(p.id); }}
                >
                  <td data-label="HC" style={{...bodyGridStyle, padding:'6px 10px', fontWeight:600}}>{p.hc || '-'}</td>
                  <td data-label={t('patientList.name')} style={{...bodyGridStyle, padding:'6px 10px', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={p.name}>{p.name}</td>
                  <td data-label={t('patientList.dob')} style={{...bodyGridStyle, padding:'6px 10px'}}>{formatDate(p.dateOfBirth, '-')}</td>
                  <td data-label={t('patientList.status')} style={{...bodyGridStyle, padding:'6px 10px'}}><StatusBadge value={p.statusName || p.status} /></td>
                  {(() => {
                    const rawBalance = p.balanceDue ?? 0;
                    const safeBalance = Math.max(0, rawBalance);
                    const owesMoney = safeBalance > 0.0001;
                    return (
                      <td data-label={t('patientList.balance')}
                        style={{
                          ...bodyGridStyle,
                          padding:'6px 10px',
                          textAlign:'right',
                          fontVariantNumeric:'tabular-nums',
                          color: owesMoney ? '#ef4444' : theme.text,
                          fontWeight: owesMoney ? 700 : 500
                        }}
                        title={owesMoney ? 'Outstanding balance' : undefined}
                      >
                        {currencyFormatter.format(safeBalance)}
                      </td>
                    );
                  })()}
                  <td data-label={t('patientList.notes')} style={{...bodyGridStyle, borderRight:'none', padding:'6px 10px', overflow:'hidden', textOverflow:'ellipsis'}} title={p.notes}>{p.notes}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

